import { infiniteQueryOptions, queryOptions, useQuery } from '@tanstack/react-query';

import { EhrAccessMemberStatus } from 'chaika-enums';
import { EhrAccessDecision } from 'chaika-types';
import { EhrAccessBlacklistQuery, EhrAccessGrantListQuery } from 'chaika-frontend-types/frontend-cms';

import { EhrAccessApi } from '@api/EhrAccessApi';
import { queryClient } from '@store/queryClient';
import { STALE_TIME_ONE_MINUTE_MS } from '@utils/staleTime';

const HAS_MANY_STALE_TIME = 60_000;

const PAGE_SIZE = 50;

interface GrantsListParams extends Omit<EhrAccessGrantListQuery, 'limit' | 'offset'> {}

interface BlacklistListParams extends Omit<EhrAccessBlacklistQuery, 'limit' | 'offset'> {}

export const ehrAccessKeys = {
    all: ['ehrAccess'] as const,
    hasMany: (ehrIds: string[]) => [...ehrAccessKeys.all, 'hasMany', ehrIds] as const,
    members: (ehrId: string) => [...ehrAccessKeys.all, 'members', ehrId] as const,
    ownEhrIds: (userId: string) => [...ehrAccessKeys.all, 'ownEhrIds', userId] as const,
    grantsInfiniteList: (query: EhrAccessGrantListQuery) =>
        [...ehrAccessKeys.all, 'grants', 'infiniteList', { query }] as const,
    blacklistInfiniteList: (query: EhrAccessBlacklistQuery) =>
        [...ehrAccessKeys.all, 'blacklist', 'infiniteList', { query }] as const,
};

export const ehrAccessQueryOptions = {
    hasMany: (ehrIds: string[]) => {
        const normalizedIds = [...new Set(ehrIds)].sort();

        return queryOptions({
            queryKey: ehrAccessKeys.hasMany(normalizedIds),
            queryFn: () => EhrAccessApi.hasMany(normalizedIds),
            enabled: normalizedIds.length > 0,
            staleTime: HAS_MANY_STALE_TIME,
        });
    },
    members: (ehrId: string) =>
        queryOptions({
            queryKey: ehrAccessKeys.members(ehrId),
            queryFn: () => EhrAccessApi.members(ehrId),
            staleTime: 0,
            gcTime: 0,
            retry: false,
        }),
    ownEhrIds: (userId: string) =>
        queryOptions({
            queryKey: ehrAccessKeys.ownEhrIds(userId),
            queryFn: () => EhrAccessApi.ownEhrIds(userId),
        }),
    grantsInfiniteList: (params: GrantsListParams) =>
        infiniteQueryOptions({
            staleTime: STALE_TIME_ONE_MINUTE_MS,
            queryKey: ehrAccessKeys.grantsInfiniteList({ ...params, limit: PAGE_SIZE }),
            queryFn: ({ pageParam }) => EhrAccessApi.grants({ ...params, limit: PAGE_SIZE, offset: pageParam }),
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                const loaded = allPages.reduce((total, page) => total + page.grants.length, 0);
                return loaded < lastPage.count ? loaded : undefined;
            },
        }),
    blacklistInfiniteList: (params: BlacklistListParams) =>
        infiniteQueryOptions({
            staleTime: STALE_TIME_ONE_MINUTE_MS,
            queryKey: ehrAccessKeys.blacklistInfiniteList({ ...params, limit: PAGE_SIZE }),
            queryFn: ({ pageParam }) => EhrAccessApi.blacklist({ ...params, limit: PAGE_SIZE, offset: pageParam }),
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) => {
                const loaded = allPages.reduce((total, page) => total + page.entries.length, 0);
                return loaded < lastPage.count ? loaded : undefined;
            },
        }),
};

// 'What is my access level to these EHRs?'
export function useHasManyAccess(ehrIds: string[]) {
    return useQuery(ehrAccessQueryOptions.hasMany(ehrIds));
}

// 'What is my access status (and deny reason) for a single EHR?'
export function useEhrAccessStatus(ehrId: string | undefined): EhrAccessDecision | undefined {
    const { data } = useHasManyAccess(ehrId ? [ehrId] : []);

    return ehrId ? data?.[ehrId] : undefined;
}

interface EhrAccessGrantParams {
    ehrId: string;
    doctorId: string;
}

// 'Does the doctor already hold an open grant to this EHR?'
export async function hasActiveEhrAccess({ ehrId, doctorId }: EhrAccessGrantParams): Promise<boolean> {
    try {
        const members = await queryClient.fetchQuery(ehrAccessQueryOptions.members(ehrId));

        return members.some(member => member.userId === doctorId && member.status === EhrAccessMemberStatus.Active);
    } catch (error) {
        console.error(error);

        return false;
    }
}
